import axiosInstance from "@/services/axiosInstance";
import { axiosParamsSerialiser } from "@/services/axiosParamsSerialiser";
import type {
    TaskResult,
    Task,
    PaginatedTaskList,
    PatchedTask,
    TaskStatusEnum,
    TaskPriorityEnum,
    TaskAttachment,
} from "@/types/api/data-contracts";
import { pollEndpoint } from "@/utils/api/pollEndpoint";
import type { AxiosPromise } from "axios";

export interface TaskCreatePayload {
    title: string;
    description?: string;
    due_date?: string | null;
    status?: TaskStatusEnum;
    priority?: TaskPriorityEnum;
    is_private?: boolean;
    assignee_uuid?: string | null;
    watcher_uuids?: string[];
    target_type?: string | null;
    target_uuid?: string | null;
}

export interface TaskListParams {
    page?: number;
    page_size?: number;
    status?: TaskStatusEnum | TaskStatusEnum[];
    priority?: TaskPriorityEnum;
    is_private?: boolean;
    assignee_uuid?: string | null;
    target_type?: string | null;
    target_uuid?: string | null;
    search?: string;
}

class TaskService {
    // eslint-disable-next-line no-use-before-define
    private static instance: TaskService;

    public static getInstance(): TaskService {
        if (!TaskService.instance) {
            TaskService.instance = new TaskService();
        }
        return TaskService.instance;
    }

    /**
     * Checks the status of an existing task, it will keep polling until a non-404 error occur ot the polling will
     * respond with status equal SUCCESS or FAILURE.
     * If a 404 error occurs means that the task has not yet being created and the polling will continue.
     * @param {string} taskUuid
     *
     * @return {Promise<TaskResult>>}
     */
    pollTask = (taskUuid: string): Promise<TaskResult> =>
        pollEndpoint<TaskResult>(axiosInstance, `tasks/tasks/${taskUuid}/`);

    list(params?: TaskListParams): AxiosPromise<PaginatedTaskList> {
        return axiosInstance.get("tasks/", {
            params,
            paramsSerializer: axiosParamsSerialiser,
        });
    }

    get(taskUuid: string): AxiosPromise<Task> {
        return axiosInstance.get(`tasks/${taskUuid}/`);
    }

    create(payload: TaskCreatePayload): AxiosPromise<Task> {
        return axiosInstance.post("tasks/", payload);
    }

    patch(taskUuid: string, payload: PatchedTask): AxiosPromise<Task> {
        return axiosInstance.patch(`tasks/${taskUuid}/`, payload);
    }

    delete(taskUuid: string): AxiosPromise<void> {
        return axiosInstance.delete(`tasks/${taskUuid}/`);
    }

    // Legacy entity-scoped aliases (used by existing widgets — do not remove)
    listEntityTasks(params: {
        target_type: string;
        target_uuid: string;
        status?: TaskStatusEnum | TaskStatusEnum[];
        assignee_uuid?: string;
        priority?: TaskPriorityEnum;
        is_private?: boolean;
    }): AxiosPromise<PaginatedTaskList> {
        return axiosInstance.get("tasks/", {
            params,
            paramsSerializer: axiosParamsSerialiser,
        });
    }

    getEntityTask(taskUuid: string): AxiosPromise<Task> {
        return axiosInstance.get(`tasks/${taskUuid}/`);
    }

    createEntityTask(payload: TaskCreatePayload): AxiosPromise<Task> {
        return axiosInstance.post("tasks/", payload);
    }

    updateEntityTask(
        taskUuid: string,
        payload: PatchedTask
    ): AxiosPromise<Task> {
        return axiosInstance.patch(`tasks/${taskUuid}/`, payload);
    }

    deleteEntityTask(taskUuid: string): AxiosPromise<void> {
        return axiosInstance.delete(`tasks/${taskUuid}/`);
    }

    listMyTasks(params?: {
        page?: number;
        page_size?: number;
        status?: "open" | "in_progress" | "done";
        hide_done?: boolean;
    }): AxiosPromise<PaginatedTaskList> {
        return axiosInstance.get("tasks/me/", { params });
    }

    listActivity(taskUuid: string): AxiosPromise<unknown[]> {
        return axiosInstance.get(`tasks/${taskUuid}/activity/`);
    }

    addAttachment(
        taskUuid: string,
        payload: { filename: string; gcs_key: string }
    ): AxiosPromise<TaskAttachment> {
        return axiosInstance.post(`tasks/${taskUuid}/attachments/`, payload);
    }

    deleteAttachment(
        taskUuid: string,
        attachmentUuid: string
    ): AxiosPromise<void> {
        return axiosInstance.delete(
            `tasks/${taskUuid}/attachments/${attachmentUuid}/`
        );
    }
}

export default TaskService;
